/**
 * useTutorDrawerState — Reader.tsx's bookkeeping for the "chat sul blocco"
 * side drawer's lifecycle (contract, not to be reinterpreted):
 *
 *   - It never opens on its own — only via `openForBlock`, fired from a
 *     block's "Chat" action (BlockActionsMenu.tsx's `onChat`).
 *   - Single conversation at a time: `openForBlock` while a drawer already
 *     exists (open OR minimized) destroys it and mounts a brand-new one —
 *     implemented via a monotonically-increasing `key`, which Reader.tsx
 *     passes straight to React's `key` prop on `<TutorDrawer key={...}>` so
 *     React unmounts the old instance (and its `useTutorConversation` state)
 *     and mounts a clean one.
 *   - Closing (`minimize`) does NOT destroy anything — `drawerChat` stays
 *     set, only `drawerMinimized` flips. Reader.tsx keeps `<TutorDrawer>`
 *     MOUNTED while minimized (see TutorDrawer.tsx's width-collapse
 *     mechanism) so in-memory state (draft, messages, streaming) survives.
 *   - Navigating away loses the drawer entirely — Reader.tsx calls
 *     `resetForNavigation` from an effect keyed on the current section id.
 */

import { useCallback, useRef, useState } from 'react'
import type { TutorOverlayFocusBlock } from './tutorViewModel.js'

export interface TutorDrawerChat {
  /** Bumped on every `openForBlock` call — passed as the React `key` for
   *  `<TutorDrawer>` so a new chat-from-block always remounts a clean
   *  instance instead of reusing a previous block's conversation state. */
  key: number
  focusBlock: TutorOverlayFocusBlock
}

export interface UseTutorDrawerStateResult {
  drawerChat: TutorDrawerChat | null
  drawerMinimized: boolean
  /** Starts (or restarts) a chat for this block. Always creates a NEW
   *  `key`, destroying any existing drawer (open or minimized) and
   *  replacing it with a fresh one, open. */
  openForBlock: (focusBlock: TutorOverlayFocusBlock) => void
  /** Collapses the open drawer to its edge tab — does not destroy state. */
  minimize: () => void
  /** Restores a minimized drawer to its open panel. */
  restore: () => void
  /** Drops the drawer entirely (e.g. on navigation to a different section).
   *  No-op if there is no drawer. */
  resetForNavigation: () => void
}

export function useTutorDrawerState(): UseTutorDrawerStateResult {
  const [drawerChat, setDrawerChat] = useState<TutorDrawerChat | null>(null)
  const [drawerMinimized, setDrawerMinimized] = useState(false)
  const nextKeyRef = useRef(0)

  const openForBlock = useCallback((focusBlock: TutorOverlayFocusBlock) => {
    const key = nextKeyRef.current
    nextKeyRef.current += 1
    setDrawerChat({ key, focusBlock })
    setDrawerMinimized(false)
  }, [])

  const minimize = useCallback(() => setDrawerMinimized(true), [])
  const restore = useCallback(() => setDrawerMinimized(false), [])

  const resetForNavigation = useCallback(() => {
    setDrawerChat(null)
    setDrawerMinimized(false)
  }, [])

  return { drawerChat, drawerMinimized, openForBlock, minimize, restore, resetForNavigation }
}
